<div class="product-management">
  <!-- Header Section -->
  <div class="page-header">
    <div class="header-content">
      <div class="header-left">
        <h1 class="page-title">
          <i class="fas fa-box"></i>
          Product Management
        </h1>
        <p class="page-subtitle">Manage your product catalog and inventory</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" (click)="openAddModal(addProductModal)">
          <i class="fas fa-plus"></i>
          Add New Product
        </button>
      </div>
    </div>
  </div>

  <!-- Search and Filters Section -->
  <div class="filters-section">
    <div class="filters-grid">
      <div class="search-box">
        <div class="input-group">
          <span class="input-group-text">
            <i class="fas fa-search"></i>
          </span>
          <input type="text" class="form-control" placeholder="Search products by name or description..."
            [(ngModel)]="searchTerm" (input)="filterProducts()">
        </div>
      </div>

      <div class="filter-controls">
        <select class="form-select" [(ngModel)]="selectedDoseType" (change)="filterProducts()">
          <option value="all">All Dose Types</option>
          @for (doseType of getAvailableDoseTypes(); track doseType) {
            <option [value]="doseType">{{ getDoseTypeText(doseType) }}</option>
          }
        </select>

        <select class="form-select" [(ngModel)]="selectedStatus" (change)="filterProducts()">
          <option value="all">All Status</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  @if (loading) {
  <div class="loading-state">
    <div class="spinner"></div>
    <p>Loading products...</p>
  </div>
  }

  <!-- Error State -->
  @if (error && !loading) {
  <div class="error-state">
    <div class="error-content">
      <i class="fas fa-exclamation-triangle fa-2x"></i>
      <h3>Failed to load products</h3>
      <p>{{ error }}</p>
      <button class="btn btn-primary" (click)="loadProducts()">
        <i class="fas fa-redo"></i>
        Try Again
      </button>
    </div>
  </div>
  }

  <!-- Products Table -->
  @if (!loading && !error) {
  <div class="products-table-container">
    <div class="table-header">
      <div class="table-info">
        <span class="total-count">{{ filteredProducts.length }} products found</span>
        @if (filteredProducts.length > itemsPerPage) {
        <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
        }
      </div>
    </div>

    @if (filteredProducts.length === 0) {
    <div class="no-products">
      <i class="fas fa-box-open fa-3x"></i>
      <h3>No products found</h3>
      <p>Try adjusting your search criteria or add a new product.</p>
      <button class="btn btn-primary" (click)="openAddModal(addProductModal)">
        <i class="fas fa-plus"></i>
        Add First Product
      </button>
    </div>
    } @else {
    <div class="table-responsive">
      <table class="table table-hover">
        <thead class="table-header">
          <tr>
            <th>Product</th>
            <th>Dose Type</th>
            <th>Price</th>
            <th>Stock</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (product of paginatedProducts; track product.id) {
          <tr class="product-row">
            <td class="product-info">
              <div class="product-image" *ngIf="getFirstImage(product)">
                <img
                  [src]="getImageUrl(getFirstImage(product), product.name, product.doseType)"
                  [alt]="product.name">
              </div>
              <div class="product-details">
                <h4 class="product-name">{{ product.name }}</h4>
                <p class="product-description">{{ product.description }}</p>
                <div class="product-meta">
                  <span class="product-id">
                    <i class="fas fa-hashtag me-1"></i>
                    ID: {{ product.id }}
                  </span>
                  @if (product.badge) {
                  <span class="product-badge">{{ product.badge }}</span>
                  }
                </div>
              </div>
            </td>

            <td class="dose-info">
              <div class="dose-display">
                <span class="dose-badge" 
                      [ngClass]="getDoseTypeClass(product.doseType)"
                      [style.--dose-color-primary]="getDoseTypeColors(product.doseType).primary"
                      [style.--dose-color-secondary]="getDoseTypeColors(product.doseType).secondary">
                  {{ getDoseTypeText(product.doseType) }}
                </span>
                <small class="dose-number">Dose {{ product.doseNumber }}</small>
                <!-- @if (product.badge && product.badge !== getDoseTypeText(product.doseType)) {
                  <span class="custom-badge">{{ product.badge }}</span>
                } -->
              </div>
            </td>

            <td class="price-info">
              @if (product.price) {
              <div class="current-price">₹{{ product.price }}</div>
              @if (product.originalPrice && product.originalPrice > product.price) {
              <div class="original-price">₹{{ product.originalPrice }}</div>
                }
              } @else {
                <div class="text-muted">
                  <small>Price set via Category Management</small>
                </div>
              }
            </td>

            <td class="stock-info">
              <div class="stock-status" [ngClass]="product.inStock ? 'in-stock' : 'out-of-stock'">
                <i [class]="product.inStock ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
                {{ product.inStock ? 'In Stock' : 'Out of Stock' }}
              </div>
            </td>

            <td class="status-info">
              <div class="status-badge" [ngClass]="product.isActive ? 'active' : 'inactive'">
                <i [class]="product.isActive ? 'fas fa-toggle-on' : 'fas fa-toggle-off'"></i>
                {{ product.isActive ? 'Active' : 'Inactive' }}
              </div>
            </td>

            <td class="actions">
              <div class="action-buttons">
                <button class="btn btn-sm btn-outline-primary" (click)="openEditModal(editProductModal, product)"
                  title="Edit Product">
                  <i class="fas fa-edit"></i>
                </button>

                <button class="btn btn-sm btn-outline-danger" (click)="openDeleteModal(deleteProductModal, product)"
                  title="Delete Product">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
          }
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    @if (totalPages > 1) {
    <div class="pagination-container">
      <nav aria-label="Products pagination">
        <ul class="pagination justify-content-center">
          <li class="page-item" [class.disabled]="currentPage === 1">
            <button class="page-link" (click)="changePage(currentPage - 1)" [disabled]="currentPage === 1">
              <i class="fas fa-chevron-left"></i>
            </button>
          </li>

          @for (page of [].constructor(totalPages); track $index) {
          <li class="page-item" [class.active]="($index + 1) === currentPage">
            <button class="page-link" (click)="changePage($index + 1)">
              {{ $index + 1 }}
            </button>
          </li>
          }

          <li class="page-item" [class.disabled]="currentPage === totalPages">
            <button class="page-link" (click)="changePage(currentPage + 1)" [disabled]="currentPage === totalPages">
              <i class="fas fa-chevron-right"></i>
            </button>
          </li>
        </ul>
      </nav>
    </div>
    }
    }
  </div>
  }

  <!-- Add/Edit Product Modal -->
  <ng-template #addProductModal let-modal>
    <div class="modal-header">
      <h4 class="modal-title">
        <i class="fas fa-plus"></i>
        {{ isEditing ? 'Edit Product' : 'Add New Product' }}
      </h4>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>
    
    <!-- Product ID Information (only show for new products) -->
    <!-- @if (!isEditing) {
    <div class="modal-body border-bottom bg-light">
      <div class="row">
        <div class="col-12">
          <div class="alert alert-info mb-0 py-2">
            <i class="fas fa-info-circle me-2"></i>
            <strong>Next Product ID:</strong> 
            <span class="badge bg-primary ms-2">{{ getNextSequentialProductId() }}</span>
            <small class="d-block mt-1 text-muted">
              Products are assigned sequential IDs automatically (1, 2, 3, etc.)
            </small>
          </div>
        </div>
      </div>
    </div>
    } -->
    
    @if (productForm) {
    <div class="modal-body">
      <form [formGroup]="productForm" (ngSubmit)="saveProduct()">
        <div class="row">
          <!-- Basic Information -->
          <div class="col-md-6">
            <h5 class="form-section-title">Basic Information</h5>

            <div class="mb-3">
              <label class="form-label">Product Name *</label>
              <input type="text" class="form-control" formControlName="name" placeholder="Enter product name">
              @if (productForm.get('name')?.invalid && productForm.get('name')?.touched) {
              <div class="invalid-feedback">
                Product name is required and must be at least 3 characters.
              </div>
              }
            </div>

            <div class="mb-3">
              <label class="form-label">Description *</label>
              <textarea class="form-control" formControlName="description" rows="3"
                placeholder="Enter product description"></textarea>
              @if (productForm.get('description')?.invalid && productForm.get('description')?.touched) {
              <div class="invalid-feedback">
                Description is required and must be at least 10 characters.
              </div>
              }
            </div>

            <!-- Multilingual Content Button -->
            <div class="mb-3">
              <div class="alert alert-info py-2">
                <div class="d-flex align-items-center justify-content-between">
                  <div>
                    <i class="fas fa-globe me-2"></i>
                    <strong>Add translations for Hindi, Marathi and Kannada</strong>
                  </div>
                  <button type="button" class="btn btn-outline-primary btn-sm" (click)="openMultilingualModal(multilingualModal)">
                    <i class="fas fa-edit me-1"></i>
                    Edit Translations
                  </button>
                </div>
              </div>
            </div>

          </div>

          <!-- Dose Configuration -->
          <div class="col-md-6">
            <h5 class="form-section-title">Dose Configuration</h5>

            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Dose Type *</label>
                  <input type="text" class="form-control" formControlName="doseType" placeholder="e.g., Green, Yellow, Red, or custom type">
                  <small class="form-text text-muted">Enter the dose type (e.g., Green, Yellow, Red, or any custom type)</small>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Dose Number *</label>
                  <input type="number" class="form-control" formControlName="doseNumber" 
                         placeholder="Enter sequence number" min="1">
                  <small class="form-text text-muted">Sequence number for this dose</small>
                  
                  <!-- Current Sequence Display -->
                  @if (getCurrentProductSequence().length > 0) {
                    <div class="mt-2">
                      <small class="text-muted">
                        <strong>Current Sequence:</strong><br>
                        @for (item of getCurrentProductSequence(); track item.doseNumber) {
                          <span class="badge bg-secondary me-1 mb-1">
                            {{ item.doseNumber }}: {{ item.doseType }}
                          </span>
                          <br>
                        }
                      </small>
                    </div>
                  }
                  
                  <!-- Dose Number Validation Warning -->
                  @if (productForm.get('doseNumber')?.touched && productForm.get('doseNumber')?.value) {
                    @if (isDoseNumberInUse(productForm.get('doseNumber')?.value, editingProductId || undefined)) {
                      <div class="alert alert-warning mt-2 py-2">
                        <small>
                          <i class="fas fa-exclamation-triangle me-1"></i>
                          <strong>Warning:</strong> Dose number {{ productForm.get('doseNumber')?.value }} is already in use.
                          <br>
                          <strong>Suggested alternatives:</strong> 
                          {{ getSuggestedDoseNumbers(productForm.get('doseNumber')?.value, editingProductId || undefined).join(', ') }}
                        </small>
                      </div>
                    }
                  }
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Badge</label>
              <input type="text" class="form-control" formControlName="badge" placeholder="e.g., DOSE 1, NEW, etc.">
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" formControlName="inStock" id="inStock">
                  <label class="form-check-label" for="inStock">In Stock</label>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" formControlName="isActive" id="isActive">
                  <label class="form-check-label" for="isActive">Active</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Features and Benefits -->
        <div class="row">
          <div class="col-md-6">
            <h5 class="form-section-title">Features *</h5>
            <div class="mb-3">
              <textarea class="form-control" formControlName="features" rows="3"
                placeholder="Enter features separated by commas"></textarea>
              <small class="form-text text-muted">Separate multiple features with commas</small>
            </div>
          </div>

          <div class="col-md-6">
            <h5 class="form-section-title">Benefits</h5>
            <div class="mb-3">
              <textarea class="form-control" formControlName="benefits" rows="3"
                placeholder="Enter benefits separated by commas"></textarea>
              <small class="form-text text-muted">Separate multiple benefits with commas</small>
            </div>
          </div>
        </div>

        <!-- Ingredients and Usage -->
        <div class="row">
          <div class="col-md-6">
            <h5 class="form-section-title">Ingredients</h5>
            <div class="mb-3">
              <textarea class="form-control" formControlName="ingredients" rows="3"
                placeholder="Enter ingredients separated by commas"></textarea>
              <small class="form-text text-muted">Separate multiple ingredients with commas</small>
            </div>
          </div>

          <div class="col-md-6">
            <h5 class="form-section-title">Usage Instructions</h5>
            <div class="mb-3">
              <textarea class="form-control" formControlName="usage" rows="3"
                placeholder="Enter usage instructions separated by commas"></textarea>
              <small class="form-text text-muted">Separate multiple instructions with commas</small>
            </div>
          </div>
        </div>

        <!-- Product Images -->
        <div class="row">
          <div class="col-12">
            <h5 class="form-section-title">Product Images</h5>
            <div class="mb-3">
              <label class="form-label">Upload Images *</label>
              <div class="image-input-container">
                <!-- File Upload Section -->
                <div class="file-upload-section mb-3">
                  <div class="input-group">
                    <input type="file" class="form-control" multiple accept="image/*,.svg" (change)="onFileSelected($event)"
                      #fileInput>
                    <button type="button" class="btn btn-outline-primary" (click)="fileInput.click()">
                      <i class="fas fa-upload"></i>
                      Choose Files
                    </button>
                  </div>
                  <small class="form-text text-muted">Select image files (JPEG, PNG, WebP, SVG). Max size: 5MB per
                    file.</small>
                </div>



                <!-- New Image Previews -->
                <div class="new-image-previews" *ngIf="imagePreviewUrls.length > 0">
                  <h6 class="text-muted mb-2">New Images to Upload:</h6>
                  <div class="image-preview-grid">
                    <div class="image-preview" *ngFor="let previewUrl of imagePreviewUrls; let i = index">
                      <img [src]="previewUrl" [alt]="'New image ' + (i + 1)" class="preview-img" style="max-width: 50%;">
                      <div class="image-actions">
                        <button type="button" class="btn btn-sm btn-outline-danger" (click)="removeSelectedImage(i)">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Existing Images (for edit mode) -->
                <div class="existing-images" *ngIf="isEditing && productForm.get('image')?.value?.length > 0">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <h6 class="text-muted mb-0">Current Images:</h6>
                    <button type="button" class="btn btn-sm btn-outline-warning" (click)="clearAllExistingImages()" 
                            [disabled]="isClearingAllImages || isDeletingImage"
                            title="{{ isClearingAllImages ? 'Clearing images...' : 'Remove all existing images to upload new ones' }}">
                      <i class="fas" [class.fa-trash-alt]="!isClearingAllImages" 
                         [class.fa-spinner]="isClearingAllImages" 
                         [class.fa-spin]="isClearingAllImages"></i> 
                      {{ isClearingAllImages ? 'Clearing...' : 'Clear All' }}
                    </button>
                  </div>
                  <div class="existing-images-grid" style="display: grid !important; grid-template-columns: repeat(3, 1fr) !important; gap: 1rem !important; width: 100% !important;">
                    <div class="existing-image-item" *ngFor="let imgUrl of productForm.get('image')?.value; let i = index" 
                         style="display: flex !important; flex-direction: column !important; border: 2px solid #e9ecef !important; border-radius: 8px !important; background: white !important; min-height: 200px !important;">
                      <div class="image-container" style="height: 150px !important; overflow: hidden !important; display: flex !important; align-items: center !important; justify-content: center !important; background: #f8f9fa !important;">
                        <img [src]="getImageUrl(imgUrl, productForm.get('name')?.value, productForm.get('doseType')?.value)"
                             [alt]="'Current image ' + (i + 1)" 
                             class="existing-image"
                             style="width: 100% !important; height: 100% !important; object-fit: cover !important;">
                      </div>
                      <div class="image-actions-below" style="padding: 0.75rem !important; background: #f8f9fa !important; border-top: 1px solid #e9ecef !important; display: flex !important; justify-content: center !important; align-items: center !important;">
                        <button type="button" class="btn btn-sm btn-outline-danger delete-image-btn" 
                                (click)="removeExistingImage(i)" 
                                [disabled]="isDeletingImage || (productForm.get('image')?.value?.length <= 1)"
                                title="{{ productForm.get('image')?.value?.length <= 1 ? 'Cannot delete the last image' : 'Remove this image' }}"
                                [style]="getDeleteButtonStyle(i)">
                          <i class="fas" [class.fa-trash]="!isDeletingImage || deletingImageIndex !== i" 
                             [class.fa-spinner]="isDeletingImage && deletingImageIndex === i" 
                             [class.fa-spin]="isDeletingImage && deletingImageIndex === i"></i>
                          {{ isDeletingImage && deletingImageIndex === i ? 'Deleting...' : 'Delete' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Combo Pricing -->
        <div class="row">
          <div class="col-12">
            <h5 class="form-section-title">Combo Pricing</h5>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Two Doses Price (₹)</label>
                  <input type="number" class="form-control" formControlName="twoDosesPrice" placeholder="0.00"
                    step="0.01">
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Three Doses Price (₹)</label>
                  <input type="number" class="form-control" formControlName="threeDosesPrice" placeholder="0.00"
                    step="0.01">
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  }
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
      <button type="button" class="btn btn-primary" (click)="saveProduct()" [disabled]="!hasValidImages() || productForm.get('name')?.invalid || productForm.get('description')?.invalid || isUploading">
        <i class="fas" [class.fa-save]="!isUploading" [class.fa-spinner]="isUploading" [class.fa-spin]="isUploading"></i>
        {{ isUploading ? 'Uploading ' + totalUploadProgress + '%...' : (isEditing ? 'Update Product' : 'Create Product') }}
      </button>
    </div>
  </ng-template>

  <!-- Edit Product Modal -->
  <ng-template #editProductModal let-modal>
    <div class="modal-header">
      <h4 class="modal-title">
        <i class="fas fa-edit"></i>
        Edit Product
      </h4>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>
    <div class="modal-body">
      @if (productForm) {
      <form [formGroup]="productForm" (ngSubmit)="saveProduct()">
        <!-- Same form content as add modal -->
        <div class="row">
          <!-- Basic Information -->
          <div class="col-md-6">
            <h5 class="form-section-title">Basic Information</h5>

            <div class="mb-3">
              <label class="form-label">Product Name *</label>
              <input type="text" class="form-control" formControlName="name" placeholder="Enter product name">
              @if (productForm.get('name')?.invalid && productForm.get('name')?.touched) {
              <div class="invalid-feedback">
                Product name is required and must be at least 3 characters.
              </div>
              }
            </div>

            <div class="mb-3">
              <label class="form-label">Description *</label>
              <textarea class="form-control" formControlName="description" rows="3"
                placeholder="Enter product description"></textarea>
              @if (productForm.get('description')?.invalid && productForm.get('description')?.touched) {
              <div class="invalid-feedback">
                Description is required and must be at least 10 characters.
              </div>
              }
            </div>

            <!-- Multilingual Content Button -->
            <div class="mb-3">
              <div class="alert alert-info py-2">
                <div class="d-flex align-items-center justify-content-between">
                  <div>
                    <i class="fas fa-globe me-2"></i>
                    <strong>Add translations for Hindi, Marathi and Kannada</strong>
                  </div>
                  <button type="button" class="btn btn-outline-primary btn-sm" (click)="openMultilingualModal(multilingualModal)">
                    <i class="fas fa-edit me-1"></i>
                    Edit Translations
                  </button>
                </div>
              </div>
            </div>

          </div>

          <!-- Dose Configuration -->
          <div class="col-md-6">
            <h5 class="form-section-title">Dose Configuration</h5>

            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Dose Type *</label>
                  <input type="text" class="form-control" formControlName="doseType" placeholder="e.g., Green, Yellow, Red, or custom type">
                  <small class="form-text text-muted">Enter the dose type (e.g., Green, Yellow, Red, or any custom type)</small>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Dose Number *</label>
                  <input type="number" class="form-control" formControlName="doseNumber" 
                         placeholder="Enter sequence number" min="1">
                  <small class="form-text text-muted">Sequence number for this dose</small>
                  
                  <!-- Current Sequence Display -->
                  @if (getCurrentProductSequence().length > 0) {
                    <div class="mt-2">
                      <small class="text-muted">
                        <strong>Current Sequence:</strong><br>
                        @for (item of getCurrentProductSequence(); track item.doseNumber) {
                          <span class="badge bg-secondary me-1 mb-1">
                            {{ item.doseNumber }}: {{ item.doseType }}
                          </span>
                          <br>
                        }
                      </small>
                    </div>
                  }
                  
                  <!-- Dose Number Validation Warning -->
                  @if (productForm.get('doseNumber')?.touched && productForm.get('doseNumber')?.value) {
                    @if (isDoseNumberInUse(productForm.get('doseNumber')?.value, editingProductId || undefined)) {
                      <div class="alert alert-warning mt-2 py-2">
                        <small>
                          <i class="fas fa-exclamation-triangle me-1"></i>
                          <strong>Warning:</strong> Dose number {{ productForm.get('doseNumber')?.value }} is already in use.
                          <br>
                          <strong>Suggested alternatives:</strong> 
                          {{ getSuggestedDoseNumbers(productForm.get('doseNumber')?.value, editingProductId || undefined).join(', ') }}
                        </small>
                      </div>
                    }
                  }
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Badge</label>
              <input type="text" class="form-control" formControlName="badge" placeholder="e.g., DOSE 1, NEW, etc.">
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" formControlName="inStock" id="editInStock">
                  <label class="form-check-label" for="editInStock">In Stock</label>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" formControlName="isActive" id="editIsActive">
                  <label class="form-check-label" for="editIsActive">Active</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Features and Benefits -->
        <div class="row">
          <div class="col-md-6">
            <h5 class="form-section-title">Features *</h5>
            <div class="mb-3">
              <textarea class="form-control" formControlName="features" rows="3"
                placeholder="Enter features separated by commas"></textarea>
              <small class="form-text text-muted">Separate multiple features with commas</small>
            </div>
          </div>

          <div class="col-md-6">
            <h5 class="form-section-title">Benefits</h5>
            <div class="mb-3">
              <textarea class="form-control" formControlName="benefits" rows="3"
                placeholder="Enter benefits separated by commas"></textarea>
              <small class="form-text text-muted">Separate multiple benefits with commas</small>
            </div>
          </div>
        </div>

        <!-- Ingredients and Usage -->
        <div class="row">
          <div class="col-md-6">
            <h5 class="form-section-title">Ingredients</h5>
            <div class="mb-3">
              <textarea class="form-control" formControlName="ingredients" rows="3"
                placeholder="Enter ingredients separated by commas"></textarea>
              <small class="form-text text-muted">Separate multiple ingredients with commas</small>
            </div>
          </div>

          <div class="col-md-6">
            <h5 class="form-section-title">Usage Instructions</h5>
            <div class="mb-3">
              <textarea class="form-control" formControlName="usage" rows="3"
                placeholder="Enter usage instructions separated by commas"></textarea>
              <small class="form-text text-muted">Separate multiple instructions with commas</small>
            </div>
          </div>
        </div>

        <!-- Product Images -->
        <div class="row">
          <div class="col-12">
            <h5 class="form-section-title">Product Images</h5>
            <div class="mb-3">
              <label class="form-label">Upload Images *</label>
              <div class="image-input-container">
                <!-- File Upload Section -->
                <div class="file-upload-section mb-3">
                  <div class="input-group">
                    <input type="file" class="form-control" multiple accept="image/*,.svg" (change)="onFileSelected($event)"
                      #fileInput>
                    <button type="button" class="btn btn-outline-primary" (click)="fileInput.click()">
                      <i class="fas fa-upload"></i>
                      Choose Files
                    </button>
                  </div>
                  <small class="form-text text-muted">Select image files (JPEG, PNG, WebP, SVG). Max size: 5MB per
                    file.</small>
                </div>



                <!-- New Image Previews -->
                <div class="new-image-previews" *ngIf="imagePreviewUrls.length > 0">
                  <h6 class="text-muted mb-2">New Images to Upload:</h6>
                  <div class="image-preview-grid">
                    <div class="image-preview" *ngFor="let previewUrl of imagePreviewUrls; let i = index">
                      <img [src]="previewUrl" [alt]="'New image ' + (i + 1)" class="preview-img">
                      <div class="image-actions">
                        <button type="button" class="btn btn-sm btn-outline-danger" (click)="removeSelectedImage(i)">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Existing Images (for edit mode) -->
                <div class="existing-images" *ngIf="isEditing && productForm.get('image')?.value?.length > 0">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <h6 class="text-muted mb-0">Current Images:</h6>
                    <button type="button" class="btn btn-sm btn-outline-warning" (click)="clearAllExistingImages()" 
                            [disabled]="isClearingAllImages || isDeletingImage"
                            title="{{ isClearingAllImages ? 'Clearing images...' : 'Remove all existing images to upload new ones' }}">
                      <i class="fas" [class.fa-trash-alt]="!isClearingAllImages" 
                         [class.fa-spinner]="isClearingAllImages" 
                         [class.fa-spin]="isClearingAllImages"></i> 
                      {{ isClearingAllImages ? 'Clearing...' : 'Clear All' }}
                    </button>
                  </div>
                  <div class="existing-images-grid" style="display: grid !important; grid-template-columns: repeat(3, 1fr) !important; gap: 1rem !important; width: 100% !important;">
                    <div class="existing-image-item" *ngFor="let imgUrl of productForm.get('image')?.value; let i = index" 
                         style="display: flex !important; flex-direction: column !important; border: 2px solid #e9ecef !important; border-radius: 8px !important; background: white !important; min-height: 200px !important;">
                      <div class="image-container" style="overflow: hidden !important; display: flex !important; align-items: center !important; justify-content: center !important; background: #f8f9fa !important;">
                        <img [src]="getImageUrl(imgUrl, productForm.get('name')?.value, productForm.get('doseType')?.value)"
                             [alt]="'Current image ' + (i + 1)" 
                             class="existing-image"
                             style="width: 100% !important; height: 100% !important; object-fit: cover !important;">
                      </div>
                      <div class="image-actions-below" style="padding: 0.75rem !important; background: #f8f9fa !important; border-top: 1px solid #e9ecef !important; display: flex !important; justify-content: center !important; align-items: center !important;">
                        <button type="button" class="btn btn-sm btn-outline-danger delete-image-btn" 
                                (click)="removeExistingImage(i)" 
                                [disabled]="isDeletingImage || (productForm.get('image')?.value?.length <= 1)"
                                title="{{ productForm.get('image')?.value?.length <= 1 ? 'Cannot delete the last image' : 'Remove this image' }}"
                                [style]="getDeleteButtonStyle(i)">
                          <i class="fas" [class.fa-trash]="!isDeletingImage || deletingImageIndex !== i" 
                             [class.fa-spinner]="isDeletingImage && deletingImageIndex === i" 
                             [class.fa-spin]="isDeletingImage && deletingImageIndex === i"></i>
                          {{ isDeletingImage && deletingImageIndex === i ? 'Deleting...' : 'Delete' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Combo Pricing -->
        <div class="row">
          <div class="col-12">
            <h5 class="form-section-title">Combo Pricing</h5>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Two Doses Price (₹)</label>
                  <input type="number" class="form-control" formControlName="twoDosesPrice" placeholder="0.00"
                    step="0.01">
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Three Doses Price (₹)</label>
                  <input type="number" class="form-control" formControlName="threeDosesPrice" placeholder="0.00"
                    step="0.01">
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
      }

    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
      <button type="button" class="btn btn-primary" (click)="saveProduct()" [disabled]="!hasValidImages() || productForm.get('name')?.invalid || productForm.get('description')?.invalid || isUploading">
        <i class="fas" [class.fa-save]="!isUploading" [class.fa-spinner]="isUploading" [class.fa-spin]="isUploading"></i>
        {{ isUploading ? 'Uploading ' + totalUploadProgress + '%...' : 'Update Product' }}
      </button>
    </div>
  </ng-template>

  <!-- Delete Confirmation Modal -->
  <ng-template #deleteProductModal let-modal>
    <div class="modal-header">
      <h4 class="modal-title text-danger">
        <i class="fas fa-exclamation-triangle"></i>
        Confirm Deletion
      </h4>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>
    <div class="modal-body">
      <div class="delete-warning">
        <i class="fas fa-exclamation-triangle fa-3x text-warning"></i>
        <h5>Are you sure you want to delete this product?</h5>
        @if (selectedProduct) {
        <div class="product-to-delete">
          <strong>{{ selectedProduct.name }}</strong>
          <p class="text-muted">{{ selectedProduct.description }}</p>
        </div>
        }
        <p class="text-danger">
          <strong>Warning:</strong> This action cannot be undone. The product will be permanently removed from the
          system.
        </p>
      </div>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
      <button type="button" class="btn btn-danger" (click)="deleteProduct(selectedProduct?.id || '')">
        <i class="fas fa-trash"></i>
        Delete Product
      </button>
    </div>
  </ng-template>

  <!-- Multilingual Content Modal -->
  <ng-template #multilingualModal let-modal>
    <div class="modal-header">
      <h4 class="modal-title">
        <i class="fas fa-globe"></i>
        Edit Multilingual Content
      </h4>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>
    <div class="modal-body">
      <form [formGroup]="multilingualForm" (ngSubmit)="saveMultilingualContent()">
        <!-- Language Tabs -->
        <ul class="nav nav-tabs mb-4" id="languageTabs" role="tablist">
          <li class="nav-item" role="presentation">
            <button class="nav-link active" id="hindi-tab" data-bs-toggle="tab" data-bs-target="#hindi" type="button" role="tab">
              <i class="fas fa-flag me-1"></i>हिंदी (Hindi)
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link" id="marathi-tab" data-bs-toggle="tab" data-bs-target="#marathi" type="button" role="tab">
              <i class="fas fa-flag me-1"></i>मराठी (Marathi)
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link" id="kannada-tab" data-bs-toggle="tab" data-bs-target="#kannada" type="button" role="tab">
              <i class="fas fa-flag me-1"></i>ಕನ್ನಡ (Kannada)
            </button>
          </li>
        </ul>

        <div class="tab-content" id="languageTabContent">
          <!-- Hindi Tab -->
          <div class="tab-pane fade show active" id="hindi" role="tabpanel">
            <div class="row">
              <div class="col-12">
                <div class="mb-3">
                  <label class="form-label">Product Name (हिंदी)</label>
                  <input type="text" class="form-control" formControlName="hi_name" placeholder="Enter product name in Hindi">
                </div>
                <div class="mb-3">
                  <label class="form-label">Description (हिंदी)</label>
                  <textarea class="form-control" formControlName="hi_description" rows="3" placeholder="Enter description in Hindi"></textarea>
                </div>
                <div class="mb-3">
                  <label class="form-label">Features (हिंदी)</label>
                  <textarea class="form-control" formControlName="hi_features" rows="3" placeholder="Enter features separated by commas"></textarea>
                  <small class="form-text text-muted">Separate multiple features with commas</small>
                </div>
                <div class="mb-3">
                  <label class="form-label">Benefits (हिंदी)</label>
                  <textarea class="form-control" formControlName="hi_benefits" rows="3" placeholder="Enter benefits separated by commas"></textarea>
                  <small class="form-text text-muted">Separate multiple benefits with commas</small>
                </div>
                <div class="mb-3">
                  <label class="form-label">Usage Instructions (हिंदी)</label>
                  <textarea class="form-control" formControlName="hi_usage" rows="3" placeholder="Enter usage instructions separated by commas"></textarea>
                  <small class="form-text text-muted">Separate multiple instructions with commas</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Marathi Tab -->
          <div class="tab-pane fade" id="marathi" role="tabpanel">
            <div class="row">
              <div class="col-12">
                <div class="mb-3">
                  <label class="form-label">Product Name (मराठी)</label>
                  <input type="text" class="form-control" formControlName="mr_name" placeholder="Enter product name in Marathi">
                </div>
                <div class="mb-3">
                  <label class="form-label">Description (मराठी)</label>
                  <textarea class="form-control" formControlName="mr_description" rows="3" placeholder="Enter description in Marathi"></textarea>
                </div>
                <div class="mb-3">
                  <label class="form-label">Features (मराठी)</label>
                  <textarea class="form-control" formControlName="mr_features" rows="3" placeholder="Enter features separated by commas"></textarea>
                  <small class="form-text text-muted">Separate multiple features with commas</small>
                </div>
                <div class="mb-3">
                  <label class="form-label">Benefits (मराठी)</label>
                  <textarea class="form-control" formControlName="mr_benefits" rows="3" placeholder="Enter benefits separated by commas"></textarea>
                  <small class="form-text text-muted">Separate multiple benefits with commas</small>
                </div>
                <div class="mb-3">
                  <label class="form-label">Usage Instructions (मराठी)</label>
                  <textarea class="form-control" formControlName="mr_usage" rows="3" placeholder="Enter usage instructions separated by commas"></textarea>
                  <small class="form-text text-muted">Separate multiple instructions with commas</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Kannada Tab -->
          <div class="tab-pane fade" id="kannada" role="tabpanel">
            <div class="row">
              <div class="col-12">
                <div class="mb-3">
                  <label class="form-label">Product Name (ಕನ್ನಡ)</label>
                  <input type="text" class="form-control" formControlName="kn_name" placeholder="Enter product name in Kannada">
                </div>
                <div class="mb-3">
                  <label class="form-label">Description (ಕನ್ನಡ)</label>
                  <textarea class="form-control" formControlName="kn_description" rows="3" placeholder="Enter description in Kannada"></textarea>
                </div>
                <div class="mb-3">
                  <label class="form-label">Features (ಕನ್ನಡ)</label>
                  <textarea class="form-control" formControlName="kn_features" rows="3" placeholder="Enter features separated by commas"></textarea>
                  <small class="form-text text-muted">Separate multiple features with commas</small>
                </div>
                <div class="mb-3">
                  <label class="form-label">Benefits (ಕನ್ನಡ)</label>
                  <textarea class="form-control" formControlName="kn_benefits" rows="3" placeholder="Enter benefits separated by commas"></textarea>
                  <small class="form-text text-muted">Separate multiple benefits with commas</small>
                </div>
                <div class="mb-3">
                  <label class="form-label">Usage Instructions (ಕನ್ನಡ)</label>
                  <textarea class="form-control" formControlName="kn_usage" rows="3" placeholder="Enter usage instructions separated by commas"></textarea>
                  <small class="form-text text-muted">Separate multiple instructions with commas</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
      <button type="button" class="btn btn-primary" (click)="saveMultilingualContent()">
        <i class="fas fa-save"></i>
        Save Translations
      </button>
    </div>
  </ng-template>
</div>